import { useCallback, useEffect, useState } from "react";
import { Archive, Calendar as CalendarIcon, Check, Lightbulb, Loader2, Save, Sparkles, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/gp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const CATEGORIES = [
  ["game_tips", "Game tips & strategy"],
  ["walkthrough", "Walkthrough / help for stuck gamers"],
  ["yorick_advice", "Yorick AI gaming advice"],
  ["yorick_daily_challenge", "Yorick AI daily challenge"],
  ["gpp_challenge", "GamePlatPlay challenges & competitions"],
  ["points_rewards", "Points & rewards promo"],
  ["educational", "Educational (school-subject tie-in)"],
  ["platform_awareness", "Platform awareness"],
];

const STATUS_LABEL = {
  idea: "Idea", awaiting_approval: "Needs approval", approved: "Approved",
  scheduled: "Scheduled", published: "Published", archived: "Archived", rejected: "Rejected",
};

const NEXT_STATUS = {
  idea: ["awaiting_approval", "rejected"],
  awaiting_approval: ["approved", "rejected"],
  approved: ["scheduled", "awaiting_approval"],
  scheduled: ["published", "approved"],
  published: ["archived"],
  rejected: ["idea"],
  archived: ["idea"],
};

const METRIC_FIELDS = ["views", "likes", "comments", "subscribers_gained", "clicks_to_gpp", "avg_view_duration_seconds"];
const BLANK_GEN = { category: "game_tips", content_type: "short", game: "", topic_title: "", topic_hook: "", notes: "", calendar_date: "", upload_ids: [] };
const BLANK_METRICS = { views: 0, likes: 0, comments: 0, subscribers_gained: 0, clicks_to_gpp: 0, avg_view_duration_seconds: 0, notes: "" };

export default function YoutubeContentAdmin({ uploads = [] }) {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [gen, setGen] = useState(BLANK_GEN);
  const [ideas, setIdeas] = useState([]);
  const [ideaBusy, setIdeaBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [metrics, setMetrics] = useState(BLANK_METRICS);
  const [metricsBusy, setMetricsBusy] = useState(false);
  const [calendar, setCalendar] = useState([]);
  const [recommendations, setRecommendations] = useState([]);

  const load = useCallback(() => {
    const q = statusFilter ? `?status=${statusFilter}` : "";
    fetch(`${API}/admin/youtube/items${q}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setItems(d.items || []))
      .catch(() => toast.error("Could not load YouTube content items."));
  }, [statusFilter]);

  const loadCalendar = useCallback(() => {
    fetch(`${API}/admin/youtube/calendar`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setCalendar(d.calendar || []))
      .catch(() => {});
  }, []);

  const loadRecommendations = useCallback(() => {
    fetch(`${API}/admin/youtube/recommendations`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setRecommendations(d.recommendations || []))
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCalendar(); loadRecommendations(); }, [loadCalendar, loadRecommendations]);

  const toggleUpload = (id) => setGen((old) => ({
    ...old,
    upload_ids: old.upload_ids.includes(id) ? old.upload_ids.filter((x) => x !== id) : [...old.upload_ids, id].slice(-6),
  }));

  const selectItem = (item) => { setSelected(item); setEditForm(item); setMetrics(item.metrics || BLANK_METRICS); };

  const brainstorm = async () => {
    setIdeaBusy(true);
    try {
      const res = await fetch(`${API}/admin/youtube/topics/generate`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category: gen.category, game: gen.game, count: 5, notes: gen.notes }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not brainstorm topics.");
      setIdeas(data.ideas || []);
    } catch (e) { toast.error(e.message); } finally { setIdeaBusy(false); }
  };

  const applyIdea = (idea) => setGen((old) => ({ ...old, topic_title: idea.title, topic_hook: idea.hook, content_type: idea.content_type, game: idea.game || old.game }));

  const generateDraft = async () => {
    if (!gen.topic_title.trim()) { toast.error("Give the video a working title first (brainstorm or type your own)."); return; }
    setGenBusy(true);
    try {
      const res = await fetch(`${API}/admin/youtube/generate`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...gen, calendar_date: gen.calendar_date || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Draft generation failed.");
      toast.success("Draft generated — review it before approving.");
      setIdeas([]);
      load();
      selectItem(data.item);
    } catch (e) { toast.error(e.message); } finally { setGenBusy(false); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setSaveBusy(true);
    try {
      const payload = { ...editForm, tags: Array.isArray(editForm.tags) ? editForm.tags : String(editForm.tags).split(",").map((t) => t.trim()).filter(Boolean) };
      const res = await fetch(`${API}/admin/youtube/items/${encodeURIComponent(selected.id)}`, {
        method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Save failed.");
      toast.success("Saved as a new version, awaiting approval.");
      selectItem(data.item); load();
    } catch (e2) { toast.error(e2.message); } finally { setSaveBusy(false); }
  };

  const setStatus = async (status) => {
    if (!selected) return;
    try {
      const res = await fetch(`${API}/admin/youtube/items/${encodeURIComponent(selected.id)}/status`, {
        method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Status change failed.");
      toast.success(`Marked ${STATUS_LABEL[status] || status}.`);
      selectItem(data.item); load(); loadCalendar();
    } catch (e) { toast.error(e.message); }
  };

  const saveMetrics = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setMetricsBusy(true);
    try {
      const numeric = Object.fromEntries(METRIC_FIELDS.map((k) => [k, Number(metrics[k]) || 0]));
      const res = await fetch(`${API}/admin/youtube/items/${encodeURIComponent(selected.id)}/metrics`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...numeric, notes: metrics.notes || "" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not save metrics.");
      toast.success("Performance metrics saved."); selectItem(data.item); load(); loadRecommendations();
    } catch (e2) { toast.error(e2.message); } finally { setMetricsBusy(false); }
  };

  return (
    <section className="glass rounded-3xl p-6 md:p-7 mb-8 border border-gp-gold/25" data-testid="admin-youtube">
      <div>
        <h2 className="font-unbounded font-bold text-xl">YouTube content studio (Yorick AI)</h2>
        <p className="text-sm text-white/55 mt-1">Brainstorm, draft, review, and approve GamePlatPlay / Yorick AI videos. Nothing here publishes to YouTube automatically — every status change is your call.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-5">
        <div className="rounded-2xl border border-white/10 bg-black/20 p-4 space-y-3">
          <div className="flex items-center gap-2 text-gp-gold text-sm font-semibold"><Sparkles size={15} /> Ideate & draft</div>
          <div className="grid sm:grid-cols-2 gap-2">
            <select value={gen.category} onChange={(e) => setGen((o) => ({ ...o, category: e.target.value }))} className="rounded-md border border-white/10 bg-gp-deep px-3 py-2 text-sm">
              {CATEGORIES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
            <select value={gen.content_type} onChange={(e) => setGen((o) => ({ ...o, content_type: e.target.value }))} className="rounded-md border border-white/10 bg-gp-deep px-3 py-2 text-sm">
              <option value="short">Short (under ~2 min)</option>
              <option value="long">Long-form</option>
            </select>
          </div>
          <Input value={gen.game} onChange={(e) => setGen((o) => ({ ...o, game: e.target.value }))} placeholder="Game / franchise (optional)" className="bg-black/25 border-white/10" />
          <Textarea value={gen.notes} onChange={(e) => setGen((o) => ({ ...o, notes: e.target.value }))} placeholder="Notes for Yorick (optional)" className="bg-black/25 border-white/10" rows={2} />
          <Button type="button" onClick={brainstorm} disabled={ideaBusy} variant="outline" className="rounded-full border-white/15 w-full">
            {ideaBusy ? <Loader2 className="animate-spin mr-2" size={15} /> : <Lightbulb className="mr-2" size={15} />}Brainstorm 5 topic ideas
          </Button>
          {ideas.length > 0 && <div className="space-y-2 max-h-56 overflow-y-auto gp-scroll">
            {ideas.map((idea, i) => <button key={i} type="button" onClick={() => applyIdea(idea)} className="w-full text-left rounded-lg border border-white/10 bg-black/25 p-2.5 hover:border-gp-gold/40">
              <p className="text-sm font-semibold">{idea.title}</p>
              <p className="text-xs text-white/55 mt-0.5">{idea.angle}</p>
            </button>)}
          </div>}
          <div className="pt-2 border-t border-white/10 space-y-2">
            <Input value={gen.topic_title} onChange={(e) => setGen((o) => ({ ...o, topic_title: e.target.value }))} placeholder="Working title" className="bg-black/25 border-white/10" />
            <Input value={gen.topic_hook} onChange={(e) => setGen((o) => ({ ...o, topic_hook: e.target.value }))} placeholder="Opening hook (optional)" className="bg-black/25 border-white/10" />
            <Input type="date" value={gen.calendar_date} onChange={(e) => setGen((o) => ({ ...o, calendar_date: e.target.value }))} className="bg-black/25 border-white/10" />
            {uploads.length > 0 && <div>
              <p className="text-xs text-white/50 mb-1">Ground it in real footage (optional, up to 6):</p>
              <div className="flex flex-wrap gap-1.5">
                {uploads.map((u) => <button key={u.id} type="button" onClick={() => toggleUpload(u.id)} className={`text-xs rounded-full px-2.5 py-1 border ${gen.upload_ids.includes(u.id) ? "border-gp-gold bg-gp-gold/15 text-gp-gold" : "border-white/15 text-white/60"}`}>{(u.notes || u.original_filename || u.id).slice(0, 28)}</button>)}
              </div>
            </div>}
            <Button type="button" onClick={generateDraft} disabled={genBusy} className="rounded-full bg-gp-gold text-black w-full">
              {genBusy ? <Loader2 className="animate-spin mr-2" size={15} /> : <Sparkles className="mr-2" size={15} />}Generate full script + metadata
            </Button>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-black/20 p-4 space-y-3">
          <div className="flex items-center gap-2 text-gp-blue text-sm font-semibold"><CalendarIcon size={15} /> Next 14 days</div>
          <div className="space-y-1.5 max-h-44 overflow-y-auto gp-scroll">
            {calendar.map((slot) => <div key={slot.date} className="flex items-center justify-between text-xs rounded-lg border border-white/10 bg-black/20 px-2.5 py-1.5">
              <span className="text-white/60">{slot.date} · {slot.content_type}</span>
              <span className="text-white/85">{slot.items.length ? `${slot.items.length} item(s)` : slot.category}</span>
            </div>)}
          </div>
          <div className="flex items-center gap-2 text-gp-green text-sm font-semibold pt-1"><TrendingUp size={15} /> What to make more of</div>
          <div className="space-y-1.5 max-h-40 overflow-y-auto gp-scroll">
            {recommendations.map((rec) => <div key={rec.category} className="text-xs rounded-lg border border-white/10 bg-black/20 px-2.5 py-1.5">
              <span className="font-semibold">{rec.category}</span>
              <p className="text-white/55 mt-0.5">{rec.reason}</p>
            </div>)}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6 mt-6">
        <div className="space-y-2">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full rounded-md border border-white/10 bg-gp-deep px-3 py-2 text-sm">
            <option value="">All statuses</option>
            {Object.entries(STATUS_LABEL).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
          <div className="space-y-2 max-h-[520px] overflow-y-auto gp-scroll pr-1">
            {items.map((item) => <button key={item.id} type="button" onClick={() => selectItem(item)} className={`w-full rounded-xl border p-3 text-left ${selected?.id === item.id ? "border-gp-gold bg-gp-gold/10" : "border-white/10 bg-black/20"}`}>
              <div className="flex justify-between gap-2"><span className="font-semibold text-sm line-clamp-1">{item.title}</span><span className="text-xs text-white/40">v{item.version}</span></div>
              <p className="text-xs text-white/55 mt-1">{item.category} · {item.content_type}</p>
              <p className="text-xs mt-1 text-gp-gold">{STATUS_LABEL[item.status] || item.status}</p>
            </button>)}
            {items.length === 0 && <p className="text-sm text-white/40 p-3">No items yet — generate one above.</p>}
          </div>
        </div>

        {editForm ? <form onSubmit={saveEdit} className="space-y-3">
          <Input required value={editForm.title} onChange={(e) => setEditForm((o) => ({ ...o, title: e.target.value }))} placeholder="Title" className="bg-black/25 border-white/10" />
          <Input required value={editForm.hook} onChange={(e) => setEditForm((o) => ({ ...o, hook: e.target.value }))} placeholder="Hook (first line on screen)" className="bg-black/25 border-white/10" />
          <Textarea required value={editForm.script} onChange={(e) => setEditForm((o) => ({ ...o, script: e.target.value }))} placeholder="Script" className="bg-black/25 border-white/10 font-mono text-xs" rows={10} />
          <Textarea required value={editForm.description} onChange={(e) => setEditForm((o) => ({ ...o, description: e.target.value }))} placeholder="YouTube description" className="bg-black/25 border-white/10" rows={4} />
          <Input required value={Array.isArray(editForm.tags) ? editForm.tags.join(", ") : editForm.tags} onChange={(e) => setEditForm((o) => ({ ...o, tags: e.target.value }))} placeholder="Tags, comma separated" className="bg-black/25 border-white/10" />
          <div className="grid sm:grid-cols-2 gap-3">
            <Input required value={editForm.thumbnail_concept} onChange={(e) => setEditForm((o) => ({ ...o, thumbnail_concept: e.target.value }))} placeholder="Thumbnail concept" className="bg-black/25 border-white/10" />
            <Input required value={editForm.thumbnail_text} onChange={(e) => setEditForm((o) => ({ ...o, thumbnail_text: e.target.value }))} placeholder="Thumbnail text overlay" className="bg-black/25 border-white/10" />
          </div>
          <Textarea required value={editForm.cta} onChange={(e) => setEditForm((o) => ({ ...o, cta: e.target.value }))} placeholder="Call to action (must mention GamePlatPlay.com)" className="bg-black/25 border-white/10" rows={2} />
          <Input value={editForm.educational_tiein || ""} onChange={(e) => setEditForm((o) => ({ ...o, educational_tiein: e.target.value }))} placeholder="Educational tie-in (educational pillar only)" className="bg-black/25 border-white/10" />
          <Input type="date" value={editForm.calendar_date || ""} onChange={(e) => setEditForm((o) => ({ ...o, calendar_date: e.target.value }))} className="bg-black/25 border-white/10" />

          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={saveBusy} className="rounded-full bg-gp-blue">
              {saveBusy ? <Loader2 className="animate-spin mr-2" size={15} /> : <Save className="mr-2" size={15} />}Save new version
            </Button>
            {(NEXT_STATUS[selected?.status] || []).map((next) => <Button key={next} type="button" onClick={() => setStatus(next)} variant="outline" className="rounded-full border-white/15">
              {next === "published" ? <Check size={15} className="mr-2" /> : next === "archived" ? <Archive size={15} className="mr-2" /> : null}Mark {STATUS_LABEL[next]}
            </Button>)}
          </div>

          {selected?.status === "published" && <div className="rounded-xl border border-gp-green/25 bg-gp-green/5 p-3 space-y-2">
            <p className="text-sm font-semibold text-gp-green">Performance</p>
            <div className="grid grid-cols-3 gap-2">
              {METRIC_FIELDS.map((key) => <Input key={key} type="number" min="0" value={metrics[key]} onChange={(e) => setMetrics((o) => ({ ...o, [key]: e.target.value }))} placeholder={key.replace(/_/g, " ")} className="bg-black/25 border-white/10 text-xs" />)}
            </div>
            <Button type="button" onClick={saveMetrics} disabled={metricsBusy} variant="outline" className="rounded-full border-gp-green/30 text-gp-green">
              {metricsBusy && <Loader2 className="animate-spin mr-2" size={15} />}Save metrics
            </Button>
          </div>}
        </form> : <p className="text-sm text-white/40">Select an item to review, or generate a new one above.</p>}
      </div>
    </section>
  );
}
